import Api from './api';

import { uniqueValues } from '@/pure/Array';

const api = new Api();

export default {
  togglePoiVisibility({ commit }, type) {
    commit('togglePoiVisibility', type);
  },
  resetFilters({ state, commit }) {
    commit('updateAllFilters', state.ruleFilters);
  },
  buildFilters({ commit }, data) {
    if (!data || !data.collaborators) {
      return;
    }

    const filters = {};
    const filteredCollaborators = data.collaborators.filter(item => item.address != null && item.sites.length > 0);

    filteredCollaborators.forEach(item => {
      for (const key in item.details) {
        if (!filters[key]) {
          filters[key] = [];
        }
        filters[key].push(item.details[key]);
      }
    });
    for (const key in filters) {
      filters[key] = uniqueValues(filters[key]);
    }
    if (!data.sites) {
      return;
    }
    const value = data.sites.filter(site => site.type === 'Target').map(site => site.id);
    commit('buildFilters', filters);
    commit('updateGlobalFilters', { key: 'targetSites', value });
  },
  getStudyResult({ commit, dispatch }, data) {
    commit('updateStudyLoaded', false);
    commit('updateFetchingState', true);

    const studyId = data.id;

    return api.getStudyResult(data).then(async result => {
      const { study } = result;

      commit('updateGlobalFilters', { key: 'currentMatchingTime', value: 'public' });

      if (study.individual_transport_only) {
        commit('updateGlobalFilters', { key: 'currentMatchingTime', value: 'private' });
      }

      if (study.personalized_transport_only) {
        commit('updateGlobalFilters', { key: 'currentMatchingTime', value: 'custom' });
      }

      if (!study.withWalkingTimes && study.public_maximum_travel_time > 0) {
        await api.updateMaximumTravelTime(studyId, {
          public_maximum_travel_time: -1,
          private_maximum_travel_time: -1,
          bicycle_maximum_travel_time: -1,
          walking_maximum_travel_time: -1,
        });
      }

      if (study.withWalkingTimes && (study.maximum_travel_time_enabled === undefined || study.maximum_travel_time_enabled === null)) {
        await api.updateMaximumTravelTime(studyId, {
          maximum_travel_time_enabled: true,
          public_maximum_travel_time: study.public_maximum_travel_time,
          private_maximum_travel_time: study.private_maximum_travel_time,
          bicycle_maximum_travel_time: study.bicycle_maximum_travel_time,
          walking_maximum_travel_time: study.walking_maximum_travel_time,
        });

        study.maximum_travel_time_enabled = true;
      }

      commit('updateStudy', { study });
      commit('updateStudyLoaded', true);
      commit('updateFetchingState', false);
      dispatch('buildFilters', study);
      return result;
    });
  },
  getStudyProductionDate({ commit }, data) {
    commit('updateFetchingState', true);
    return api.getStudyProductionDate(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  updateIndex({ commit }, idx) {
    commit('updateIndex', idx);
  },
  getStudy({ commit }, data) {
    if (data) {
      commit('updateFetchingState', true);
      return api.getStudy(data).then(response => {
        // remove draft from name
        response.name = response.name.replace('_draft', '');

        commit('updateFetchingState', false);
        commit('updateRawStudy', response);
        return response;
      });
    } else {
      commit('updateRawStudy', null);
      commit('updateStudy', {
        study: null,
      });
    }
  },
  getIsochrone({ commit }, data) {
    commit('updateFetchingState', true);
    return api.getIsochrone(data).then(response => {
      commit('updateIsochrone', response.isochrones, data.isPrivate);
      commit('updateFetchingState', false);
      return response.isochrones;
    });
  },
  checkCoverage(_, data) {
    return api.checkCoverage(data).then(response => response);
  },
  clearIsochrone({ commit }) {
    commit('updateIsochrone', null);
  },
  updateIsochroneDecay({ commit }, data) {
    commit('updateIsochroneDecay', data);
  },
  updateIsochroneDisplay({ commit }, data) {
    commit('updateIsochroneDisplay', data);
  },
  updateIsochroneMode({ commit }, data) {
    commit('updateIsochroneMode', data.mode);
  },
  deleteStudy({ commit }, data) {
    commit('updateFetchingState', true);
    return api.deleteStudy(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  patchStudy({ commit }, data) {
    commit('updateFetchingState', true);
    return api.patchStudy(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  getStudyRegions({ commit }, data) {
    commit('updateFetchingState', true);
    return api.getStudyRegions(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  patchStudyRegions({ commit }, data) {
    commit('updateFetchingState', true);
    return api.patchStudyRegions(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  createStudy({ commit }, data) {
    commit('updateFetchingState', true);
    return api
      .createStudy(data)
      .then(response => response.data)
      .then(response => {
        commit('updateFetchingState', false);
        commit('updateRawStudy', response);
        return response;
      });
  },
  updateActiveAside({ commit }, aside) {
    commit('updateActiveAside', { aside });
  },
  togglePublicTransportLines({ commit }) {
    commit('togglePublicTransportLines');
  },
  toggleAirQualityIndexOverlay({ commit }) {
    commit('toggleAirQualityIndexOverlay');
  },
  updateStudy({ commit }, study) {
    commit('updateStudy', {
      study,
    });
  },
  importFile({ commit }, data) {
    commit('updateFetchingState', true);
    return api
      .importFile(data)
      .then(response => {
        commit('updateFetchingState', false);
        return response;
      })
      .catch(e => {
        commit('updateFetchingState', false);
        throw e;
      });
  },
  uploadLogo(_, data) {
    return api
      .uploadLogo(data)
      .then(response => {
        return response;
      })
      .catch(e => {
        throw e;
      });
  },
  downloadCsvFile({ commit }, data) {
    commit('updateFetchingState', true);
    return api.downloadCsvFile(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  getCurrentSites({ commit }, data) {
    commit('updateFetchingState', true);
    return api.getCurrentSites(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  patchSites({ commit }, data) {
    commit('updateFetchingState', true);
    return api.patchSites(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  deleteSite({ commit }, id) {
    commit('updateFetchingState', true);
    return api.deleteSite(id).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  postSites({ commit }, data) {
    commit('updateFetchingState', true);
    return api.postSites(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  uploadSiteImage(_, data) {
    return api.uploadSiteImage(data).then(response => response);
  },
  getShareStudy({ commit }, data) {
    commit('updateFetchingState', true);
    return api.getShareStudy(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  resetUserAccount({ commit }, data) {
    commit('updateFetchingState', true);
    return api.resetUserAccount(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  postShareStudy({ commit }, data) {
    commit('updateFetchingState', true);
    return api.postShareStudy(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  deleteShareStudyUser({ commit }, data) {
    commit('updateFetchingState', true);
    return api.deleteShareStudyUser(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  fetchProcessStatus({ commit }, data) {
    commit('updateFetchingState', true);
    return api.fetchProcessStatus(data);
  },
  startProcessing({ commit }, data) {
    commit('updateFetchingState', true);
    return api.startProcessing(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  putAddresses({ commit }, data) {
    commit('updateFetchingState', true);
    return api.putAddresses(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  geocode({ commit }, data) {
    commit('updateFetchingState', true);
    return api.geocode(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  fetchCollaborators({ commit }, data) {
    commit('updateFetchingState', true);
    return api.fetchCollaborators(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  getStudyCsvFilters({ commit }, data) {
    commit('updateFetchingState', true);
    return api.getStudyCsvFilters(data).then(response => {
      commit('updateFetchingState', false);
      return response;
    });
  },
  fetchStudies({ commit }) {
    commit('updateFetchingState', true);
    return api.fetchStudies().then(response => {
      commit('updateFetchingState', false);
      commit('updateStudies', {
        studies: response,
      });
      return response;
    });
  },
  renewExpirationDate(_, { id }) {
    return api.renewExpirationDate(id).then(response => response);
  },
  updateExpirationDate(_, data) {
    return api.updateExpirationDate(data).then(response => response);
  },
  updateStudyName(_, data) {
    return api.updateStudyName(data).then(response => response);
  },
  updateFilters({ commit }, data) {
    commit('updateFilters', data);
  },
  updateMaximumTravelTime({ state, commit }, data) {
    commit('updateFetchingState', true);
    return api
      .updateMaximumTravelTime(state.study.id, {
        maximum_travel_time_enabled: state.study.maximum_travel_time_enabled,
        public_maximum_travel_time: state.study.public_maximum_travel_time,
        private_maximum_travel_time: state.study.private_maximum_travel_time,
        bicycle_maximum_travel_time: state.study.bicycle_maximum_travel_time,
        walking_maximum_travel_time: state.study.walking_maximum_travel_time,
        ...data,
      })
      .then(response => {
        commit('updateMaximumTravelTime', response);
        return response;
      })
      .finally(() => {
        commit('updateFetchingState', false);
      });
  },
  updateTargetSitesOrder({ commit }, targetSites) {
    commit('updateSites', targetSites);

    return api.patchTargetSitesOrder(targetSites).then(response => response);
  },
  updateGlobalFilters({ commit }, data) {
    commit('updateGlobalFilters', data);
  },
  cancelLoading({ commit }) {
    commit('updateFetchingState', false);
  },
  updateCurrentModal({ commit }, data) {
    commit('updateCurrentModal', data);
  },
  updateTutorial({ commit }, data) {
    commit('updateTutorial', data);
  },
  exportStudyCsv(_, data) {
    return api.exportStudyCsv(data.studyId).then(result => result);
  },
  exportCollabCsv(_, data) {
    return api.exportCollabCsv(data.studyId).then(result => result);
  },
  uploadRawData(_, data) {
    return api.uploadRawData(data).then(response => response);
  },
  downloadRawData(_, data) {
    return api.downloadRawData(data).then(response => response);
  },
  getDraftId({ commit }, data) {
    commit('updateDuplicatingState', true);
    return api
      .getStudyDraftId(data)
      .then(response => response)
      .finally(() => commit('updateDuplicatingState', false));
  },
  createStudyDuplicate({ commit }, data) {
    commit('updateDuplicatingState', true);

    try {
      return api
        .createStudyDuplicate(data)
        .then(response => response)
        .finally(() => commit('updateDuplicatingState', false));
    } catch (e) {
      commit('updateDuplicatingState', false);
      return e;
    }
  },
};
